import { useNavigate, Link } from "@tanstack/react-router";
import { ChevronDown, LogOut, Settings, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { studio } from "@/mocks/data";
import { useMockAuth, signOut, loginForRole } from "@/lib/mock-auth";

export function ProfileMenu() {
  const navigate = useNavigate();
  const { user, session } = useMockAuth();

  const name = user?.name ?? studio.ownerName;
  const email = user?.email ?? studio.ownerEmail;
  const initials = user?.initials ?? studio.ownerInitials;
  const subtitle = user
    ? user.role === "superadmin"
      ? "Superadmin"
      : user.role === "client_portal"
      ? "Client"
      : user.role.replace("org_", "").replace("_", " ")
    : `${studio.plan} plan`;

  const settingsHref =
    user?.role === "client_portal" ? "/portal/profile" : user?.role === "superadmin" ? "/admin" : "/settings";

  const handleSignOut = () => {
    const role = session?.role;
    signOut();
    navigate({ to: role ? loginForRole(role) : "/auth", replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring">
        <div className="flex size-7 flex-none items-center justify-center rounded-full bg-surface-muted font-mono text-[10px] font-medium text-foreground/80 ring-1 ring-border">
          {initials}
        </div>
        <div className="hidden min-w-0 leading-tight sm:block">
          <p className="truncate text-xs font-medium">{name}</p>
          <p className="truncate font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            {subtitle}
          </p>
        </div>
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col leading-tight">
          <span className="text-sm font-medium">{name}</span>
          <span className="truncate text-[11px] font-normal text-muted-foreground">{email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={settingsHref} className="flex items-center gap-2">
            <User className="size-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={settingsHref} className="flex items-center gap-2">
            <Settings className="size-4" />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={handleSignOut}>
          <LogOut className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
